import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./UnverifiedUsers.css";
import Header from "../header/Header";
import Footer from "../footer/Footer";
import Sidebar from "../Sidebar";
import VivahApi from "../../api";

function ManageUsers() {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [approving, setApproving] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [selected, setSelected] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [filterPaid, setFilterPaid] = useState('all');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    setSelected([]);
    try {
      const resp = await VivahApi.Admin.listUnverifiedUsers();
      setAllUsers(resp?.data?.users || []);
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.error || err.message || "Failed to load users" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const counts = useMemo(() => ({
    all: allUsers.length,
    unverified: allUsers.filter(u => u.status !== 'CONFIRMED').length,
    deactivated: allUsers.filter(u => u.enabled === false).length,
  }), [allUsers]);

  const displayedUsers = useMemo(() => {
    let list = allUsers;
    if (activeTab === 'unverified') list = list.filter(u => u.status !== 'CONFIRMED');
    else if (activeTab === 'deactivated') list = list.filter(u => u.enabled === false);
    if (filterStatus === 'confirmed') list = list.filter(u => u.status === 'CONFIRMED' && u.enabled !== false);
    else if (filterStatus === 'unverified') list = list.filter(u => u.status !== 'CONFIRMED');
    else if (filterStatus === 'deactivated') list = list.filter(u => u.enabled === false);
    if (filterGender === 'male') list = list.filter(u => u.gender === 'M' || u.gender?.toLowerCase() === 'male');
    else if (filterGender === 'female') list = list.filter(u => u.gender === 'F' || u.gender?.toLowerCase() === 'female');
    if (filterPaid === 'paid') list = list.filter(u => u.isPaid === true);
    else if (filterPaid === 'free') list = list.filter(u => !u.isPaid);
    const q = searchText.trim().toLowerCase();
    if (q) list = list.filter(u =>
      u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
    return list;
  }, [allUsers, activeTab, searchText, filterStatus, filterGender, filterPaid]);

  function handleTabChange(tab) {
    setActiveTab(tab);
    setSelected([]);
    setSearchText('');
    setFilterStatus('all');
    setFilterGender('all');
    setFilterPaid('all');
  }

  // ── Approve ────────────────────────────────────────────────────
  async function approveUser(userId) {
    setApproving(prev => ({ ...prev, [userId]: true }));
    try {
      await VivahApi.Admin.approveUser(userId);
      setAllUsers(prev => prev.map(u =>
        u.userId === userId ? { ...u, emailVerified: true, status: 'CONFIRMED' } : u
      ));
      setSelected(prev => prev.filter(id => id !== userId));
      setMessage({ type: "success", text: "User approved — moved to All Users." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.error || err.message || "Approval failed" });
    } finally {
      setApproving(prev => ({ ...prev, [userId]: false }));
    }
  }

  async function handleBulkApprove() {
    if (selected.length === 0) return;
    setMessage({ type: "info", text: `Approving ${selected.length} user(s)…` });
    const results = await Promise.allSettled(selected.map(id => VivahApi.Admin.approveUser(id)));
    const approvedIds = selected.filter((_, i) => results[i].status === "fulfilled");
    const failed = results.filter(r => r.status === "rejected").length;
    setAllUsers(prev => prev.map(u =>
      approvedIds.includes(u.userId) ? { ...u, emailVerified: true, status: 'CONFIRMED' } : u
    ));
    setSelected([]);
    setMessage(failed > 0
      ? { type: "warning", text: `${approvedIds.length} approved, ${failed} failed.` }
      : { type: "success", text: `${approvedIds.length} user(s) approved successfully.` }
    );
  }

  // ── Deactivate / Activate ──────────────────────────────────────
  async function handleDeactivate(user) {
    if (!window.confirm(`Deactivate ${user.name || user.email}? They will lose access immediately.`)) return;
    setActionLoading(user.sub);
    try {
      await VivahApi.Admin.deactivateUser(user.sub);
      setAllUsers(prev => prev.map(u =>
        u.sub === user.sub ? { ...u, enabled: false } : u
      ));
      setMessage({ type: "success", text: `${user.name || user.email} has been deactivated.` });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.error || err.message || "Deactivation failed" });
    } finally {
      setActionLoading(null);
    }
  }

  async function handleActivate(user) {
    setActionLoading(user.sub);
    try {
      await VivahApi.Admin.activateUser(user.sub);
      setAllUsers(prev => prev.map(u =>
        u.sub === user.sub ? { ...u, enabled: true } : u
      ));
      setMessage({ type: "success", text: `${user.name || user.email} has been activated.` });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.error || err.message || "Activation failed" });
    } finally {
      setActionLoading(null);
    }
  }

  // ── Reset Password ─────────────────────────────────────────────
  function openResetModal(user) {
    setResetTarget(user);
    setResetPwd('');
    setResetConfirm('');
    setResetError(null);
  }

  function closeResetModal() {
    setResetTarget(null);
    setResetPwd('');
    setResetConfirm('');
    setResetError(null);
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setResetError(null);
    if (resetPwd !== resetConfirm) { setResetError('Passwords do not match.'); return; }
    if (resetPwd.length < 8) { setResetError('Password must be at least 8 characters.'); return; }
    setResetLoading(true);
    try {
      await VivahApi.Admin.resetUserPassword(resetTarget.sub, resetPwd);
      setMessage({ type: 'success', text: `Password reset for ${resetTarget.name || resetTarget.email}.` });
      closeResetModal();
    } catch (err) {
      setResetError(err?.response?.data?.error || err.message || 'Reset failed.');
    } finally {
      setResetLoading(false);
    }
  }

  // ── Checkboxes (unverified tab only) ──────────────────────────
  function toggleSelect(userId) {
    setSelected(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  }

  function toggleSelectAll() {
    const unverifiedInView = displayedUsers.filter(u => u.status !== 'CONFIRMED');
    if (selected.length === unverifiedInView.length && unverifiedInView.length > 0) {
      setSelected([]);
    } else {
      setSelected(unverifiedInView.map(u => u.userId));
    }
  }

  const unverifiedInView = displayedUsers.filter(u => u.status !== 'CONFIRMED');
  const allSelected = unverifiedInView.length > 0 && selected.length === unverifiedInView.length;

  function formatDate(val) {
    if (!val) return "—";
    try { return new Date(val).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch (e) { return String(val); }
  }

  const TABS = [
    { key: 'all', label: 'All Users' },
    { key: 'unverified', label: 'Unverified' },
    { key: 'deactivated', label: 'Deactivated' },
  ];

  return (
    <div className="gla_page" id="gla_page">
      <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
      <Header />
      <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
        <div className="container text-left">
          <div className="row">
            <div className="col-md-8">
              <h1 className="gla_h1_title">Manage Users</h1>
            </div>
          </div>
        </div>
      </div>

      <section id="gla_content" className="gla_content">
        <section className="gla_section">
          <div className="container">
            <div className="dashboard">
              <div className="row">
                <Sidebar />
                <div className="col-md-10 col-xs-12 dashboard_container">
                  <div className="unverified-box">

                    {/* Header row */}
                    <div className="unverified-header">
                      <div>
                        <h3 className="unverified-title">Manage Users</h3>
                        <p className="unverified-subtitle">
                          {loading ? "Loading…" : `${displayedUsers.length} shown · ${allUsers.length} total`}
                        </p>
                      </div>
                      <div className="unverified-actions">
                        <button className="button btn unverified-refresh-btn" onClick={loadUsers} disabled={loading}>
                          {loading ? "Refreshing…" : "↻ Refresh"}
                        </button>
                        {activeTab === 'unverified' && selected.length > 0 && (
                          <button className="button btn unverified-bulk-btn" onClick={handleBulkApprove}>
                            Approve Selected ({selected.length})
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="unverified-filter-tabs">
                      {TABS.map(({ key, label }) => (
                        <button
                          key={key}
                          className={`unverified-filter-tab${activeTab === key ? ' active' : ''}`}
                          onClick={() => handleTabChange(key)}
                        >
                          {label} <span className="unverified-tab-count">{counts[key]}</span>
                        </button>
                      ))}
                    </div>

                    {/* Search + filter bar */}
                    <div className="mu-search-bar">
                      <input
                        className="mu-search-input"
                        type="text"
                        placeholder="Search name or email…"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                      />
                      <select
                        className="mu-filter-select"
                        value={filterStatus}
                        onChange={e => { setFilterStatus(e.target.value); setSelected([]); }}
                      >
                        <option value="all">All Statuses</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="unverified">Unverified</option>
                        <option value="deactivated">Deactivated</option>
                      </select>
                      <select
                        className="mu-filter-select"
                        value={filterGender}
                        onChange={e => setFilterGender(e.target.value)}
                      >
                        <option value="all">All Genders</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      <select
                        className="mu-filter-select"
                        value={filterPaid}
                        onChange={e => setFilterPaid(e.target.value)}
                      >
                        <option value="all">All Plans</option>
                        <option value="paid">Paid</option>
                        <option value="free">Free</option>
                      </select>
                      {(searchText || filterStatus !== 'all' || filterGender !== 'all' || filterPaid !== 'all') && (
                        <span className="mu-search-count">
                          {displayedUsers.length} result{displayedUsers.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Status message */}
                    {message && (
                      <div className={`unverified-status unverified-status-${message.type}`}>
                        {message.text}
                      </div>
                    )}

                    {/* Table */}
                    {loading ? (
                      <div className="unverified-loading">Loading users…</div>
                    ) : displayedUsers.length === 0 ? (
                      <div className="unverified-empty">
                        <span className="unverified-empty-icon">✓</span>
                        <p>{searchText ? 'No users match your search.' : activeTab === 'unverified' ? 'No unverified users.' : activeTab === 'deactivated' ? 'No deactivated users.' : 'No users found.'}</p>
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table className="admin-users-table unverified-table">
                          <thead>
                            <tr>
                              {activeTab === 'unverified' && (
                                <th className="unverified-checkbox-col">
                                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all" />
                                </th>
                              )}
                              <th>Name</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Gender</th>
                              <th>Plan</th>
                              <th>Registered</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedUsers.map(user => (
                              <tr
                                key={user.userId}
                                className={[
                                  selected.includes(user.userId) ? 'unverified-row-selected' : '',
                                  user.enabled === false ? 'mu-row-deactivated' : ''
                                ].filter(Boolean).join(' ')}
                              >
                                {activeTab === 'unverified' && (
                                  <td className="unverified-checkbox-col">
                                    {user.status !== 'CONFIRMED' && (
                                      <input
                                        type="checkbox"
                                        checked={selected.includes(user.userId)}
                                        onChange={() => toggleSelect(user.userId)}
                                        aria-label={`Select ${user.email}`}
                                      />
                                    )}
                                  </td>
                                )}
                                <td>{user.name || <span className="unverified-empty-val">—</span>}</td>
                                <td>{user.email || <span className="unverified-empty-val">—</span>}</td>
                                <td>{user.phone || <span className="unverified-empty-val">—</span>}</td>
                                <td>
                                  {user.gender ? (() => {
                                    const g = user.gender === 'M' ? 'male' : user.gender === 'F' ? 'female' : user.gender.toLowerCase();
                                    const label = g === 'male' ? 'Male' : g === 'female' ? 'Female' : user.gender;
                                    return <span className={`mu-gender-badge mu-gender-${g}`}>{label}</span>;
                                  })() : <span className="unverified-empty-val">—</span>}
                                </td>
                                <td>
                                  <span className={`mu-plan-badge ${user.isPaid ? 'mu-plan-paid' : 'mu-plan-free'}`}>
                                    {user.isPaid ? 'Paid' : 'Free'}
                                  </span>
                                </td>
                                <td>{formatDate(user.createdAt)}</td>
                                <td>
                                  <span className={`unverified-badge unverified-badge-${(user.status || "").toLowerCase()}`}>
                                    {user.enabled === false ? 'Deactivated' : (user.status || "UNCONFIRMED")}
                                  </span>
                                </td>
                                <td>
                                  {user.enabled === false ? (
                                    <button
                                      className="button btn mu-activate-btn"
                                      disabled={actionLoading === user.sub}
                                      onClick={() => handleActivate(user)}
                                    >
                                      {actionLoading === user.sub ? "…" : "Activate"}
                                    </button>
                                  ) : user.status !== 'CONFIRMED' ? (
                                    <button
                                      className="button btn unverified-approve-btn"
                                      disabled={approving[user.userId]}
                                      onClick={() => approveUser(user.userId)}
                                    >
                                      {approving[user.userId] ? "Approving…" : "Approve"}
                                    </button>
                                  ) : (
                                    <button
                                      className="button btn mu-deactivate-btn"
                                      disabled={actionLoading === user.sub}
                                      onClick={() => handleDeactivate(user)}
                                    >
                                      {actionLoading === user.sub ? "…" : "Deactivate"}
                                    </button>
                                  )}
                                  <button
                                    className="button btn mu-reset-pwd-btn"
                                    onClick={() => openResetModal(user)}
                                  >
                                    Reset Pwd
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>
      <Footer />

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="mu-modal-backdrop" onClick={closeResetModal}>
          <div className="mu-modal" onClick={e => e.stopPropagation()}>
            <div className="mu-modal-header">
              <span>Reset Password</span>
              <button className="mu-modal-close" onClick={closeResetModal}>✕</button>
            </div>
            <div className="mu-modal-body">
              <p className="mu-modal-user">{resetTarget.name || resetTarget.email}</p>
              {resetError && <div className="mu-modal-error">{resetError}</div>}
              <form onSubmit={handleResetPassword}>
                <div className="mu-modal-field">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={resetPwd}
                    onChange={e => setResetPwd(e.target.value)}
                    placeholder="Min. 8 characters"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="mu-modal-field">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    value={resetConfirm}
                    onChange={e => setResetConfirm(e.target.value)}
                    placeholder="Repeat password"
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="mu-modal-actions">
                  <button type="button" className="button btn" onClick={closeResetModal}>Cancel</button>
                  <button type="submit" className="button btn btn-default" disabled={resetLoading}>
                    {resetLoading ? 'Resetting…' : 'Reset Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsers;
